import React, { useState } from 'react';
import { BookOpen, Mail, KeyRound, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import { confirmPasswordRecovery, isValidNewPassword, requestPasswordRecovery } from '../../../services/PasswordRecoveryService';

interface PasswordRecoveryProps {
  initialIdentifier: string;
  onBack: () => void;
  onDone: (message: string) => void;
}

/** Recuperación de contraseña en dos pasos: 1) pedir el código por correo, 2) código + contraseña nueva. */
const PasswordRecovery: React.FC<PasswordRecoveryProps> = ({ initialIdentifier, onBack, onDone }) => {
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const sendCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      await requestPasswordRecovery(identifier);
      setInfo('Si el usuario existe, recibirá un correo con un código de 8 caracteres. Caduca en 30 minutos.');
      setStep('confirm');
    } catch (err) {
      setError((err as Error)?.message || 'No se pudo solicitar el código.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    if (step === 'request') {
      await sendCode();
      return;
    }
    if (!isValidNewPassword(password)) {
      setError('La contraseña debe tener al menos 8 caracteres, una letra y un número o símbolo.');
      return;
    }
    if (password !== repeat) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await confirmPasswordRecovery(identifier, code, password);
      onDone('Contraseña actualizada. Ya puedes iniciar sesión.');
    } catch (err) {
      setError((err as Error)?.message || 'No se pudo restablecer la contraseña.');
      setIsLoading(false);
    }
  };

  return (
    <div className="right-panel">
      <div className="right-panel-content">
        <div className="mobile-header">
          <div className="mobile-logo"><BookOpen size={18} color="white" /></div>
          <h2 className="mobile-title">MARCIAL PONS</h2>
        </div>
        <div className="login-form-container">
          <div className="login-header">
            <h2>RECUPERAR ACCESO</h2>
            <p>{step === 'request'
              ? 'Introduce tu usuario o email y te enviaremos un código.'
              : 'Introduce el código recibido y elige una contraseña nueva.'}</p>
          </div>
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label">Usuario / Email</label>
              <div className="input-container group">
                <Mail size={18} className="input-icon" />
                <input
                  required
                  disabled={isLoading || step === 'confirm'}
                  className="login-input"
                  type="text"
                  value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                />
              </div>
            </div>

            {step === 'confirm' && (
              <>
                <div className="form-group">
                  <label className="form-label">Código recibido</label>
                  <div className="input-container group">
                    <KeyRound size={18} className="input-icon" />
                    <input
                      required
                      autoFocus
                      disabled={isLoading}
                      className="login-input"
                      type="text"
                      maxLength={8}
                      placeholder="ABCD1234"
                      value={code}
                      onChange={e => setCode(e.target.value.toUpperCase())}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Contraseña nueva</label>
                  <div className="input-container group">
                    <Lock size={18} className="input-icon" />
                    <input required disabled={isLoading} className="login-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Repite la contraseña</label>
                  <div className="input-container group">
                    <Lock size={18} className="input-icon" />
                    <input required disabled={isLoading} className="login-input" type="password" placeholder="••••••••" value={repeat} onChange={e => setRepeat(e.target.value)} />
                  </div>
                </div>
              </>
            )}

            {info && !error && <p className="form-label" style={{ textTransform: 'none', letterSpacing: 0 }}>{info}</p>}
            {error && <p className="form-label" style={{ color: '#f87171', textTransform: 'none', letterSpacing: 0 }}>{error}</p>}

            <button type="submit" disabled={isLoading} className={`login-button group ${isLoading ? 'is-loading' : ''}`}>
              <span className="button-text">{step === 'request' ? 'ENVIAR CÓDIGO' : 'CAMBIAR CONTRASEÑA'}</span>
              <span className="button-icon-wrapper">
                <Loader2 size={18} className="spinner-icon" />
              </span>
            </button>

            <div className="form-label-row">
              <a href="#" className="hover-underline" onClick={(e) => { e.preventDefault(); if (!isLoading) onBack(); }}>
                <ArrowLeft size={10} /> Volver al acceso
              </a>
              {step === 'confirm' && (
                <a href="#" className="hover-underline" onClick={(e) => { e.preventDefault(); if (!isLoading) sendCode(); }}>
                  Reenviar código
                </a>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordRecovery;
